import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import { motion } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { PageHeader } from "@/components/layouts/PageHeader";
import { TabPanel } from "@/components/ui/tabs/Tab";
import { cn } from "@/lib/utils";
import { ScheduledBriefingsFullscreenContext } from "@/routes/scheduled-briefings/useScheduledBriefingsFullscreen";
import { logoutUser } from "@/store/auth.async-actions";
import useAuth, { type AuthState } from "@/store/auth.slice";

const ScheduledBriefingsRoute = () => {
  const user = useAuth((state: AuthState) => state.user);
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const navRef = useRef<HTMLElement>(null);
  const tabRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });
  const [isWorkflowFullscreen, setIsWorkflowFullscreen] = useState(false);

  const isTemplateCreateRoute =
    pathname === "/scheduled-briefings/templates/create";
  // Duplicating a template loads before Template Studio mounts.
  const isFullscreen = isTemplateCreateRoute || isWorkflowFullscreen;

  const isTemplatesRoute =
    pathname === "/scheduled-briefings/templates" ||
    pathname.startsWith("/scheduled-briefings/templates/");

  const tabs = [
    {
      label: "Briefings",
      to: "/scheduled-briefings",
      isActive: !isTemplatesRoute,
    },
    {
      label: "Templates",
      to: "/scheduled-briefings/templates",
      isActive: isTemplatesRoute,
    },
  ] as const;

  const activeIndex = tabs.findIndex((tab) => tab.isActive);

  // `isFullscreen`: the tab bar remounts when a workflow page closes.
  useEffect(() => {
    if (activeIndex < 0 || !navRef.current) return;
    const el = tabRefs.current[activeIndex];
    if (!el) return;
    const nav = navRef.current.getBoundingClientRect();
    const tab = el.getBoundingClientRect();
    setIndicator({
      left: tab.left - nav.left,
      width: tab.width,
    });
  }, [activeIndex, pathname, isFullscreen]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      {isFullscreen ? null : (
        <PageHeader
          label="Scheduled Briefings"
          showFeedSwitcher={false}
          userEmail={user?.email ?? ""}
          userTenantName={user?.default_tenant?.name}
          onLogout={logoutUser}
          navigationSlot={
            <nav
              ref={navRef}
              aria-label="Scheduled Briefings"
              className="relative flex self-stretch items-end gap-6 ml-36"
            >
              {tabs.map((tab, index) => (
                <Link
                  key={tab.to}
                  ref={(el) => {
                    tabRefs.current[index] = el;
                  }}
                  to={tab.to}
                  activeOptions={{
                    exact: tab.to === "/scheduled-briefings",
                  }}
                  aria-current={tab.isActive ? "page" : undefined}
                  className={cn(
                    "flex items-center bg-transparent px-0 pt-3.5 pb-2.5 text-[13px] no-underline outline-hidden transition-colors duration-150 ease-out tracking-[0.13px]",
                    "hover:text-gray-700 focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2",
                    tab.isActive && "font-medium",
                  )}
                >
                  {tab.label}
                </Link>
              ))}
              {activeIndex >= 0 && (
                <motion.span
                  className="absolute bottom-0 z-10 h-0.5 bg-violet-800"
                  aria-hidden
                  animate={{ left: indicator.left, width: indicator.width }}
                  transition={{
                    type: "tween",
                    duration: 0.15,
                    ease: [0.25, 0.1, 0.25, 1],
                  }}
                  initial={false}
                />
              )}
            </nav>
          }
        />
      )}
      <TabPanel
        animationKey={
          isTemplatesRoute && !isTemplateCreateRoute ? "templates" : pathname
        }
        className={cn(
          isFullscreen
            ? "flex min-h-0 flex-1 flex-col overflow-hidden"
            : "overflow-auto",
        )}
      >
        <ScheduledBriefingsFullscreenContext.Provider
          value={setIsWorkflowFullscreen}
        >
          <Outlet />
        </ScheduledBriefingsFullscreenContext.Provider>
      </TabPanel>
    </div>
  );
};

export default ScheduledBriefingsRoute;
